import React, { useState, createContext, useContext } from 'react';
export type QuestionType = 'text' | 'single-select' | 'multi-select';
export type QuestionScope = 'all_orders' | 'item_triggered';
export type Question = {
  id: string;
  question: string;
  helpText?: string;
  type: QuestionType;
  options?: string[];
  required: boolean;
  scope: QuestionScope;
  itemIds?: string[];
  active: boolean;
};
type Ctx = {
  questions: Question[];
  addQuestion: (q: Omit<Question, 'id'>) => void;
  updateQuestion: (id: string, patch: Partial<Question>) => void;
  deleteQuestion: (id: string) => void;
  getQuestion: (id: string) => Question | undefined;
};
const QuestionsContext = createContext<Ctx | null>(null);
const uid = () => Math.random().toString(36).slice(2, 9);
const seed: Question[] = [{
  id: 'q_allergies',
  question: 'Any food allergies?',
  helpText: 'Please list any allergies we should be aware of.',
  type: 'text',
  required: false,
  scope: 'all_orders',
  active: true
}, {
  id: 'q_utensils',
  question: 'Do you need utensils?',
  type: 'single-select',
  options: ['Yes, please', 'No, save the plastic'],
  required: true,
  scope: 'all_orders',
  active: true
}, {
  id: 'q_steak',
  question: 'How would you like your steak cooked?',
  type: 'single-select',
  options: ['Rare', 'Medium Rare', 'Medium', 'Medium Well', 'Well Done'],
  required: true,
  scope: 'item_triggered',
  itemIds: ['m11', 'm12'],
  active: true
}];
export function QuestionsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [questions, setQuestions] = useState<Question[]>(seed);
  const addQuestion = (q: Omit<Question, 'id'>) => setQuestions(prev => [...prev, {
    ...q,
    id: `q_${uid()}`
  }]);
  const updateQuestion = (id: string, patch: Partial<Question>) => setQuestions(prev => prev.map(q => q.id === id ? {
    ...q,
    ...patch
  } : q));
  const deleteQuestion = (id: string) => setQuestions(prev => prev.filter(q => q.id !== id));
  const getQuestion = (id: string) => questions.find(q => q.id === id);
  return <QuestionsContext.Provider value={{
    questions,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    getQuestion
  }} data-id="element-2881">
      {children}
    </QuestionsContext.Provider>;
}
export function useQuestions() {
  const ctx = useContext(QuestionsContext);
  if (!ctx) throw new Error('useQuestions must be used within QuestionsProvider');
  return ctx;
}